/**
 * notify-decision.ts — Luật THUẦN: một sự kiện thông báo có đổ chuông / hiện
 * thông báo trình duyệt / nháy tiêu đề tab không. Không đụng DOM để test được.
 *
 * Máy chủ đã lọc "ai được báo" theo cài đặt tổ chức (Cài đặt → Thông báo tin
 * nhắn); ở đây chỉ còn tuỳ chọn cá nhân + ngữ cảnh của tab hiện tại.
 */
import { i18n } from '@/lib/i18n'

export const NOTIFY_TONES = [
  'ding', 'chime', 'pop', 'bell',
  // Pixabay (Universfield) — xem public/sounds/CREDITS.md
  'px-notify-09', 'px-notify-010', 'px-notify-036', 'px-ringtone-030', 'px-ringtone-050',
] as const
export type NotifyTone = (typeof NOTIFY_TONES)[number]

export const TONE_LABEL: Record<NotifyTone, string> = {
  get ding() { return i18n.t('accountPages:notifications.tones.ding') },
  get chime() { return i18n.t('accountPages:notifications.tones.chime') },
  get pop() { return i18n.t('accountPages:notifications.tones.pop') },
  get bell() { return i18n.t('accountPages:notifications.tones.bell') },
  get 'px-notify-09'() { return i18n.t('accountPages:notifications.tones.pxNotify09') },
  get 'px-notify-010'() { return i18n.t('accountPages:notifications.tones.pxNotify010') },
  get 'px-notify-036'() { return i18n.t('accountPages:notifications.tones.pxNotify036') },
  get 'px-ringtone-030'() { return i18n.t('accountPages:notifications.tones.pxRingtone030') },
  get 'px-ringtone-050'() { return i18n.t('accountPages:notifications.tones.pxRingtone050') },
}

export interface UserNotifyPrefs {
  sound: { enabled: boolean; tone: NotifyTone; volume: number }
  desktop: boolean
  titleFlash: boolean
  repeat: { mode: 'once' | 'repeat'; intervalSec: number; maxTimes: number }
  muteWhenBusy: boolean
}

export const DEFAULT_USER_NOTIFY: UserNotifyPrefs = {
  sound: { enabled: true, tone: 'ding', volume: 70 },
  desktop: false,
  titleFlash: true,
  repeat: { mode: 'once', intervalSec: 10, maxTimes: 3 },
  muteWhenBusy: false,
}

export type DesktopPermission = 'granted' | 'denied' | 'default' | 'unsupported'

export interface NotifyEvent {
  kind: 'message' | 'reminder'
  conversationId: string
  /** Nhân viên gửi (nếu có) — tin của chính mình không bao giờ đổ chuông. */
  senderUserId?: string | null
  /** Ghi chú nội bộ. */
  isInternal?: boolean
}

export interface NotifyContext {
  myUserId: string | undefined
  myPresence: 'online' | 'busy' | 'offline' | undefined
  prefs: UserNotifyPrefs
  /** document.visibilityState === 'visible' */
  tabVisible: boolean
  /** document.hasFocus() */
  tabFocused: boolean
  /** Hội thoại đang mở trên màn Hội thoại (null nếu không mở). */
  openConversationId: string | null
  desktopPermission: DesktopPermission
}

export interface NotifyDecision {
  sound: boolean
  desktop: boolean
  flash: boolean
  /** Lặp âm báo tới khi quay lại tab (theo chu kỳ âm báo). */
  repeat: boolean
}

export const NOTHING: NotifyDecision = { sound: false, desktop: false, flash: false, repeat: false }

export function decideNotify(ev: NotifyEvent, ctx: NotifyContext): NotifyDecision {
  // Tin mình gửi / ghi chú nội bộ mình viết → im lặng.
  if (ev.senderUserId && ev.senderUserId === ctx.myUserId) return NOTHING

  const tabActive = ctx.tabVisible && ctx.tabFocused
  // Đang mở đúng hội thoại và đang nhìn tab → đã thấy tin, không cần báo.
  // (Lời nhắc chưa phản hồi vẫn báo: nó nhắc việc, không phải tin mới.)
  if (ev.kind === 'message' && tabActive && ctx.openConversationId === ev.conversationId) return NOTHING

  const muted = ctx.prefs.muteWhenBusy && ctx.myPresence === 'busy'
  const sound = !muted && ctx.prefs.sound.enabled && ctx.prefs.sound.volume > 0
  return {
    sound,
    // Thông báo hệ điều hành chỉ khi không nhìn tab — nhìn tab thì đã có chuông + danh sách.
    desktop: !muted && ctx.prefs.desktop && ctx.desktopPermission === 'granted' && !tabActive,
    flash: ctx.prefs.titleFlash && !tabActive,
    repeat: sound && ctx.prefs.repeat.mode === 'repeat' && !tabActive,
  }
}

/** Chống dồn âm: tối đa 1 lần phát mỗi `gapMs` (mặc định 2 giây). */
export function canPlayNow(lastPlayedAt: number | null, now: number, gapMs = 2000): boolean {
  return lastPlayedAt === null || now - lastPlayedAt >= gapMs
}

/** Tiêu đề tab khi đang nháy: xen kẽ "(3) Tin nhắn mới…" và tiêu đề gốc. */
export function flashTitle(baseTitle: string, unread: number, tick: number): string {
  if (unread <= 0 || tick % 2 === 1) return baseTitle
  return i18n.t('accountPages:notifications.flashTitle', { count: unread > 99 ? '99+' : unread })
}

/** Hợp nhất tuỳ chọn từ máy chủ/localStorage (có thể thiếu trường) với mặc định. */
export function mergePrefs(raw: unknown): UserNotifyPrefs {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Partial<UserNotifyPrefs>
  const d = DEFAULT_USER_NOTIFY
  const tone = (NOTIFY_TONES as readonly string[]).includes(r.sound?.tone as string) ? r.sound!.tone : d.sound.tone
  const num = (v: unknown, dflt: number, min: number, max: number) =>
    typeof v === 'number' && Number.isFinite(v) ? Math.min(max, Math.max(min, v)) : dflt
  return {
    sound: {
      enabled: typeof r.sound?.enabled === 'boolean' ? r.sound.enabled : d.sound.enabled,
      tone,
      volume: num(r.sound?.volume, d.sound.volume, 0, 100),
    },
    desktop: typeof r.desktop === 'boolean' ? r.desktop : d.desktop,
    titleFlash: typeof r.titleFlash === 'boolean' ? r.titleFlash : d.titleFlash,
    repeat: {
      mode: r.repeat?.mode === 'repeat' || r.repeat?.mode === 'once' ? r.repeat.mode : d.repeat.mode,
      intervalSec: num(r.repeat?.intervalSec, d.repeat.intervalSec, 3, 300),
      maxTimes: num(r.repeat?.maxTimes, d.repeat.maxTimes, 1, 20),
    },
    muteWhenBusy: typeof r.muteWhenBusy === 'boolean' ? r.muteWhenBusy : d.muteWhenBusy,
  }
}

/** "/conversations/abc" → "abc". */
export function openConversationFromPath(pathname: string): string | null {
  const m = /^\/conversations\/([^/?#]+)/.exec(pathname)
  return m ? decodeURIComponent(m[1]) : null
}
